import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import { useNavigate } from 'react-router-dom';
import { MDropdown } from 'common';
import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { REQUIRED } from 'utils/helpers/validationMessages';
import * as R from 'ramda';
import { useVerifyCustomerIdQuery } from 'feature/loading-management/customer-authorizations/customerAuthorizationsEndpoints';
import {
  CreateCustomerAuthContext,
  initialStateStep2,
  initialStateStep3,
  initialStateStep4,
} from '../Create';
import AuthorizationVolumes from '../components/AuthorizationVolumes';

const resetState = R.mergeAll([initialStateStep4, initialStateStep3, initialStateStep2]);

const InformationForm = () => {
  const navigate = useNavigate();
  const { currentAffiliate } = useUserRolePermissions();
  const affiliatesOptions = useGetAffiliateOptions(['supplier']);
  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const {
    setIsNavHidden,
    setInfo,
    info,
    id,
    setId,
    matching,
    setMatching,
    customerIdValidation,
    setCustomerIdValidation,
  } = useContext(CreateCustomerAuthContext);
  const { controlType, customerIdName } = info;
  const isInitialRender = useRef(false);
  const [switchState, setSwitchState] = useState('GLOBAL');

  const customerValue = id || customerIdName || '';

  // setInfoAndReset :: info -> info
  const setInfoAndReset = R.o(setInfo, R.mergeLeft(resetState));

  const query = {
    customerIdName: id,
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  // Skip allows conditional fetching
  // https://redux-toolkit.js.org/rtk-query/usage/conditional-fetching
  const hasValidId = R.and(
    R.isEmpty(customerIdValidation),
    R.and(R.not(R.isEmpty(id)), id.length >= 4)
  );
  const skip = R.not(R.and(isInitialRender.current, hasValidId));
  const { data, isLoading } = useVerifyCustomerIdQuery({ params: query }, { skip });
  useEffect(() => {
    if (!isInitialRender.current) {
      isInitialRender.current = true;
    }
    if (data && data.length) setMatching(data);
    if (isNotEmpty(customerIdValidation)) setMatching([]);
  }, [data, customerIdValidation, setMatching]);

  const isCurrentInOptions = useMemo(
    () =>
      affiliatesOptions.some(
        affiliate =>
          affiliate.customerIdName === currentAffiliate?.customerIdName &&
          affiliate.dataCompanyId === currentAffiliate?.dataCompanyId
      ),
    [currentAffiliate, affiliatesOptions]
  );

  useEffect(() => {
    if (R.not(isCurrentInOptions)) {
      setCurrentSelectedAffiliate(affiliatesOptions[0]);
      navigate(
        `/affiliate/${affiliatesOptions[0].customerIdName}/${affiliatesOptions[0].dataCompanyId}/loading-management/customer-authorizations/create/customer-information`,
        {
          replace: true,
        }
      );
    }
  }, [affiliatesOptions, setCurrentSelectedAffiliate, navigate, isCurrentInOptions]);

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      affiliate =>
        split[0] === affiliate.customerIdName && parseInt(split[1], 10) === affiliate.dataCompanyId
    );
    setCurrentSelectedAffiliate(affiliateIndex);
    const affiliate = affiliates[affiliateIndex];
    navigate(
      `/affiliate/${affiliate.customerIdName}/${affiliate.dataCompanyId}/loading-management/customer-authorizations/create/customer-information`,
      {
        replace: true,
      }
    );
  };

  useEffect(() => {
    if (customerIdName && customerIdName !== id) {
      const resetForm = {
        ...info,
        address: undefined,
        address2: undefined,
        authorizationType: 'UNLIMITED',
        authorizationVolume: undefined,
        city: undefined,
        class: undefined,
        customerIdName: undefined,
        customerName: undefined,
        phoneNumber: undefined,
        renewalVolume: undefined,
        state: undefined,
        zipCode: undefined,
      };
      setInfo(resetForm);
    }
  }, [customerIdName, id, info, setInfo]);

  const isValGTE4 = val => val.length < 4 || val.length > 9;
  const isOnlyInt = val => R.not(/^[0-9]*$/.test(val));
  const startsWithZero = val => val.startsWith('0');

  const validateCustomerId = R.cond([
    [R.isEmpty, R.always(REQUIRED)],
    [isOnlyInt, R.always('Must only be numbers')],
    [startsWithZero, R.always('Customer Id cannot start with 0')],
    [isValGTE4, R.always('Must be between 4-9 digits')],
    [R.T, R.always('')],
  ]);

  const handleCustomerIdChange = R.compose(
    setCustomerIdValidation,
    validateCustomerId,
    R.tap(setId),
    R.path(['target', 'value'])
  );

  const isSinglePoint = R.equals('SINGLE', controlType);
  // Checks needed to hide New Button
  // if SINGLE point && matching list contains controls !SINGLE while matching customerIdName === id
  const isSingleAndMatches = R.and(
    isSinglePoint,
    !matching?.filter(auth => auth.controlType === 'SINGLE' && auth.customerIdName === id).length
  );
  // if GLOBAL || ALL && matching customerIdName === id
  const isGlobalAndNotExist = R.and(
    !isSinglePoint,
    !matching?.filter(auth => auth.customerIdName === id).length
  );

  const handleExistingAuth = auth => {
    setId(auth.customerIdName);
    setInfo(
      R.evolve(
        {
          address: R.always(auth.address1),
          address2: R.always(auth.address2),
          city: R.always(auth.city),
          customerIdName: R.always(auth.customerIdName),
          customerName: R.always(auth.customerName),
          phoneNumber: R.always(auth.phoneNumber),
          state: R.always(auth.state),
          zipCode: R.always(auth.zipCode),
        },
        info
      )
    );
    setTimeout(() => setMatching([]), 250);
  };

  return (
    <Card
      sx={{
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        mt: { xs: 6, lg: 9 },
        mx: 'auto',
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        p: { xs: 2, lg: 3 },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        pb: { xs: 0, lg: 3 },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        width: { xs: 1, lg: 760 },
      }}
      variant="create"
    >
      <CardContent variant="create">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            gridRowGap: 8,
          }}
        >
          <Typography variant="h5">Customer Information</Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            Please provide the following authorization details.
          </Typography>
        </Box>
        {affiliatesOptions.length > 1 && (
          <MDropdown
            id="affiliate-select"
            items={affiliatesOptions}
            label="Affiliate"
            name="affiliate"
            onChange={({ value }) => handleAffiliateChange(value)}
            required
            value={
              currentAffiliate
                ? `${currentAffiliate.customerIdName}-${currentAffiliate.dataCompanyId}`
                : ''
            }
          />
        )}
        <Typography variant="body1">
          Select the type of customer authorization you want to create.
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          onChange={(_, value) => setInfoAndReset(R.assoc('controlType', value, info))}
          value={controlType}
        >
          <ToggleButton selected={!isSinglePoint} value={switchState}>
            multiple term.
          </ToggleButton>
          <ToggleButton value="SINGLE">single term.</ToggleButton>
        </ToggleButtonGroup>
        {!isSinglePoint && (
          <FormControlLabel
            control={
              <Switch
                checked={controlType === 'GLOBAL'}
                onChange={({ target }) => {
                  const value = target.checked ? 'GLOBAL' : 'ALL';
                  const newValue = R.assoc('controlType', value, info);
                  setInfoAndReset(newValue);
                  setSwitchState(value);
                }}
              />
            }
            label="Share volume across terminals"
          />
        )}
        <AuthorizationVolumes dataPath={[]} />
        <TextField
          error={isNotEmpty(customerIdValidation)}
          helperText={customerIdValidation}
          label="Customer ID"
          name="customerIdName"
          onChange={handleCustomerIdChange}
          required
          value={customerValue}
          variant="outlined"
        />
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            {!skip && !customerIdName && (isGlobalAndNotExist || isSingleAndMatches) && (
              <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ pt: '2px' }} variant="body1">
                  {`"${id}"`}
                </Typography>
                <Button
                  onClick={() => {
                    setInfoAndReset(R.assoc('customerIdName', id, info));
                    setMatching([]);
                  }}
                  variant="text"
                >
                  Create New <AddRoundedIcon color="inherit" sx={{ mt: '-2px' }} />
                </Button>
              </Box>
            )}
            {/* if SINGLE point && matching list contains controls SINGLE while matching customerIdName === id Hide matching results */}
            {matching
              ?.filter(filtered =>
                isSinglePoint
                  ? filtered.controlType === 'SINGLE' && filtered.customerIdName !== id
                  : filtered
              )
              .map(auth => (
                <Box
                  key={auth.customerAuthorizationId}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography sx={{ pt: '2px' }} variant="body1">
                    {auth.customerIdName}
                  </Typography>
                  {R.not(isSinglePoint) && auth.controlType === 'SINGLE' ? (
                    <Button onClick={() => handleExistingAuth(auth)} variant="text">
                      Use Existing Auth.
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setIsNavHidden(false);
                        navigate('../..');
                      }}
                      variant="text"
                    >
                      Edit Existing Auth.
                    </Button>
                  )}
                </Box>
              ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InformationForm;
